import { useState, useEffect, useCallback } from "react";
import { adminApi, type FinanceRevenue } from "@/lib/superadmin";
import {
  PageHeader,
  StatCard,
  SectionCard,
  ErrorBanner,
  PageSkeleton,
  EmptyState,
  IconButton,
  AreaChart,
} from "../components/ui";

function inr(value: number): string {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value.toLocaleString("en-IN")}`;
}

export default function FinancePage() {
  const [data, setData] = useState<FinanceRevenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await adminApi.finance.revenue());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load financials.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const collectionRate =
    data && data.collected + data.outstanding > 0
      ? Math.round((data.collected / (data.collected + data.outstanding)) * 100)
      : null;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Revenue & Financials"
        subtitle="Recurring revenue, collections, and outstanding balance across all tenants"
        actions={
          <IconButton
            icon="ri-refresh-line"
            title="Refresh"
            spinning={loading}
            onClick={() => void load()}
          />
        }
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Monthly Recurring Revenue"
              value={data ? inr(data.mrr) : "—"}
              icon="ri-repeat-line"
              tone="emerald"
            />
            <StatCard
              label="Annual Recurring Revenue"
              value={data ? inr(data.arr) : "—"}
              icon="ri-funds-line"
              tone="primary"
            />
            <StatCard
              label="Collected"
              value={data ? inr(data.collected) : "—"}
              icon="ri-checkbox-circle-line"
              tone="sky"
              hint={collectionRate !== null ? `${collectionRate}% rate` : undefined}
            />
            <StatCard
              label="Outstanding"
              value={data ? inr(data.outstanding) : "—"}
              icon="ri-time-line"
              tone="amber"
            />
          </div>

          <SectionCard title="Collected by Month" icon="ri-bar-chart-box-line">
            {data?.byMonth.length ? (
              <AreaChart
                data={data.byMonth.map((m) => m.amount)}
                labels={data.byMonth.map((m) => m.month)}
                format={inr}
                height={210}
                color="oklch(0.72 0.15 160)"
              />
            ) : (
              <EmptyState
                icon="ri-bar-chart-box-line"
                title="No monthly collection data yet"
                hint="Collections will chart here as subscription payments land."
              />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
